import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { HelpCircle, ChevronDown } from 'lucide-react';

/**
 * Reusable "Comment ça marche ?" popover — italic link next to a page title
 * that opens an accordion with a curated Q&A explaining the page's features.
 *
 * Usage in a page:
 *   <div className="flex items-center gap-3">
 *     <h1>Analyse IA</h1>
 *     <HowItWorks pageKey="analysis" />
 *   </div>
 */

const PAGES = {
  analysis: {
    title: 'Analyse IA — Comment ça marche ?',
    items: [
      {
        q: 'Quel type de graphique fonctionne le mieux ?',
        a: "TradingView ou votre broker, en H1 / H4 / Daily de préférence, avec au moins 50 bougies visibles. Évitez les images floues ou tronquées — plus le contexte visible est large, plus l'IA détecte finement la structure.",
      },
      {
        q: 'Pourquoi l\'IA propose 3 TPs ?',
        a: "Pour appliquer un Money Management strict : sécuriser 25 % au TP1 (BE), 25 % au TP2, laisser courir 50 % jusqu'au TP100. Ce découpage optimise votre RR global sur la durée et vous protège émotionnellement lors des retracements.",
      },
      {
        q: 'Puis-je générer un graphique automatiquement (sans upload) ?',
        a: "Oui. Sur l'écran d'analyse, entrez juste le nom de l'actif (BTC, TSLA, EUR/USD…) et le timeframe. TRADEX AI récupère les données live, génère un graphique optimisé pour l'IA et lance l'analyse en un clic — pratique quand vous n'êtes pas devant TradingView.",
      },
      {
        q: 'Quelle différence entre "Entrée IA" et "Entrée marché" ?',
        a: "Entrée marché : vous achetez/vendez immédiatement au prix actuel — le trade devient actif tout de suite dans le journal.\n\nEntrée IA : vous placez un ordre limite au niveau optimal proposé par l'IA (souvent meilleur prix). Le trade reste en statut \"En attente\" dans le journal ; il bascule automatiquement en \"Actif\" dès que le prix atteint votre niveau d'entrée. Idéal pour éviter d'entrer trop haut/bas dans l'émotion.",
      },
      {
        q: 'Quelle différence entre un trade Swing et un trade Intraday ?',
        a: "Swing : positions tenues plusieurs jours à plusieurs semaines. TPs plus éloignés, SL plus large, moins d'aller-retours mais des gains unitaires plus gros.\n\nIntraday : trades ouverts et clôturés dans la même journée. TPs rapprochés (TP1 souvent sécurisé rapidement), SL serré, plus de trades par semaine. TRADEX AI adapte automatiquement les niveaux de TP et le RR selon le type détecté.",
      },
    ],
  },
  'scan-auto': {
    title: 'Scan Auto — Comment ça marche ?',
    items: [
      {
        q: 'Qu\'est-ce que le Scan Auto ?',
        a: "Un scanner qui surveille en continu votre watchlist (BTC, ETH, actions…) et vous notifie dès qu'un setup de qualité apparaît. Ne loupez plus les opportunités pendant votre journée de travail.",
      },
      {
        q: 'À quelle fréquence tourne-t-il ?',
        a: "Toutes les 30 min pendant les heures de marché, avec priorité aux configurations que l'IA juge \"haute conviction\". Vous recevez une notification push dès qu'un setup passe le seuil de qualité.",
      },
      {
        q: 'Comment ajouter / retirer un actif ?',
        a: "Depuis la barre \"Ajouter un asset\". Pro : 3 assets simultanés. Expert : 10 assets. Un actif retiré n'est plus scanné mais reste dans votre historique.",
      },
      {
        q: 'Qu\'est-ce que le "Récap du jour" ?',
        a: "Un résumé quotidien de tous les setups détectés par le Scan Auto sur les dernières 24h, classés par score de qualité. Pour chaque proposition, deux boutons : ✓ Accepter (le trade est créé automatiquement dans votre journal en statut \"En attente\" ou \"Actif\") ou ✗ Rejeter (le setup est archivé, ne réapparaît plus). Vous gardez le contrôle final.",
      },
      {
        q: 'Puis-je avoir 2 trades sur le même actif en même temps ?',
        a: "Non. Un seul trade actif ou en attente par actif à la fois. C'est une règle stricte de Money Management pour éviter la sur-exposition. Si le Scan Auto détecte un nouveau setup sur un actif déjà pris, il vous en notifie mais ne crée pas de doublon.",
      },
    ],
  },
  dca: {
    title: 'DCA — Comment ça marche ?',
    items: [
      {
        q: 'À quoi sert le DCA ?',
        a: "À entrer sur une position en plusieurs tranches (au lieu d'un seul coup). Réduit votre prix moyen si le marché descend, améliore votre RR global et lisse votre point d'entrée sur des marchés volatils.",
      },
      {
        q: 'Comment configurer un DCA ?',
        a: "Renseignez l'actif, votre budget total, le nombre de paliers et l'espacement (%). L'outil calcule les niveaux et la taille de chaque tranche automatiquement — il ne reste plus qu'à valider.",
      },
      {
        q: 'Où retrouver mes DCA en cours ?',
        a: "Uniquement dans l'onglet DCA → sous-onglet \"Suivi\". Les trades DCA ne s'affichent pas dans le journal principal car ils suivent une logique différente (multi-entrées, PRU moyen live, paliers non tous exécutés). Le sous-onglet \"Suivi\" affiche l'évolution du PRU moyen et le statut de chaque palier.",
      },
    ],
  },
  calculator: {
    title: 'Calculateur — Comment ça marche ?',
    items: [
      {
        q: 'À quoi sert cet outil ?',
        a: "Calculer la taille de position optimale selon votre capital, votre % de risque max par trade, et la distance entry ↔ SL. C'est le pilier du Money Management — n'entrez jamais un trade sans avoir fait ce calcul.",
      },
      {
        q: 'Quels champs remplir ?',
        a: "Capital total, % risque (typiquement 1-2 %), prix d'entrée, prix du stop loss. Le calculateur sort la quantité à trader et le montant exact que vous risquez sur ce trade.",
      },
      {
        q: 'Puis-je enregistrer mes calculs ?',
        a: "Oui, un clic sur \"Créer le trade\" transforme le calcul en trade actif dans le journal, avec tous les paramètres pré-remplis.",
      },
    ],
  },
  history: {
    title: 'Journal — Comment ça marche ?',
    items: [
      {
        q: 'Comment fonctionne le journal ?',
        a: "Il liste tous vos trades (réels + paper) avec entry / SL / TPs, PnL live et historique. Les trades actifs se mettent à jour automatiquement selon le prix marché.",
      },
      {
        q: 'Différence entre trade "Réel" et "Paper" ?',
        a: "Le Paper est pour tester des idées sans risque, il est isolé de vos stats de trading réelles. Le Réel compte dans vos performances et votre Track Record public.",
      },
      {
        q: 'Comment clôturer un trade manuellement ?',
        a: "Ouvrez le trade → bouton \"Clôturer au marché\" → confirmez le prix. Le PnL est calculé selon les TPs déjà touchés + le reste au prix marché.",
      },
      {
        q: 'Que trouve-t-on dans les stats des trades terminés ?',
        a: "Une analyse complète de vos trades clos : Win Rate (% de trades gagnants), RR moyen, PnL total et par période, temps moyen de détention, meilleur / pire trade, répartition par actif et par direction (Long / Short), performance par jour de la semaine et par timeframe. Idéal pour identifier vos forces et corriger vos biais.",
      },
    ],
  },
  'news-eco': {
    title: 'News Éco — Comment ça marche ?',
    items: [
      {
        q: 'Que contient News Éco ?',
        a: "Le calendrier économique du jour et de la semaine : FOMC, NFP, CPI, résultats des grandes entreprises. Impact prévu sur les marchés (rouge / orange / gris) et heure exacte de publication.",
      },
      {
        q: 'Pourquoi les news sont importantes pour trader ?',
        a: "Les événements économiques majeurs (annonces de la Fed, CPI, chômage US) provoquent des mouvements violents et imprévisibles. Ouvrir une position juste avant un événement à impact fort est risqué (volatilité extrême, spread élargi, slippage). Consultez News Éco chaque matin pour éviter de trader avant un événement rouge, planifier des sorties partielles avant l'annonce, ou au contraire profiter des mouvements post-annonce.",
      },
      {
        q: 'L\'IA prend-elle en compte les news dans ses analyses ?',
        a: "Oui. Avant de générer une analyse ou un setup Scan Auto, l'IA consulte le calendrier économique et intègre les événements à venir dans sa décision : elle évite de proposer une entrée juste avant un événement à impact fort (FOMC, NFP, CPI…), ajuste les niveaux de TP / SL en cas de forte volatilité attendue, et peut recommander de retarder l'entrée ou de réduire la taille de position. Vous obtenez des trades \"news-aware\" sans avoir à faire ce travail manuellement.",
      },
    ],
  },
  learn: {
    title: 'Formation — Comment ça marche ?',
    items: [
      {
        q: 'Comment sont organisés les cours ?',
        a: "Par niveau : Débutant → Intermédiaire → Avancé. Chaque module a des sections courtes (5-10 min) avec quiz de validation.",
      },
      {
        q: 'Que dois-je apprendre en priorité ?',
        a: "Le Money Management d'abord, puis la structure de marché, puis les Order Blocks / FVG. Cet ordre vous rend autonome sur les analyses le plus rapidement possible.",
      },
    ],
  },
  notifications: {
    title: 'Notifications — Comment ça marche ?',
    items: [
      {
        q: 'Quels événements sont notifiés ?',
        a: "TP touchés, SL touchés, setups Scan Auto détectés, alertes prix personnalisées, statut abonnement.",
      },
      {
        q: 'Comment activer les push notifications ?',
        a: "Depuis \"Mes paramètres → Notifications\" ou au 1er lancement. Sur iOS, ajoutez d'abord la PWA à l'écran d'accueil pour que les notifications fonctionnent en arrière-plan.",
      },
      {
        q: 'Que fait le badge rouge ?',
        a: "Compteur des notifications non lues. Cliquer sur la notif ouvre la page / le trade correspondant directement — plus besoin de chercher dans le journal.",
      },
    ],
  },
  watchlist: {
    title: 'Mes Marchés — Comment ça marche ?',
    items: [
      {
        q: 'À quoi sert l\'onglet Mes Marchés ?',
        a: "C'est votre tableau de bord des actifs que vous suivez de près : prix live, variation 24h, mini-graphiques et signaux techniques. Vue d'ensemble rapide sans ouvrir chaque graphique un par un.",
      },
      {
        q: 'Comment ajouter un actif à ma watchlist ?',
        a: "Bouton \"Ajouter un asset\", tapez le ticker (BTC, TSLA, EUR/USD…), sélectionnez la classe (Crypto / Forex / Actions / Indices). L'actif apparaît instantanément avec ses données live.",
      },
      {
        q: 'Puis-je lancer une analyse IA depuis cet onglet ?',
        a: "Oui. Cliquez sur un actif → un menu propose \"Analyser avec l'IA\" (récupère automatiquement le graphique, lance l'analyse) ou \"Ouvrir dans TradingView\" pour votre propre étude.",
      },
      {
        q: 'Combien d\'actifs puis-je surveiller ?',
        a: "Free : 3 actifs. Pro : 15 actifs. Expert : illimité. La watchlist est partagée avec le Scan Auto : ajouter un actif ici active automatiquement son scan si votre quota Scan Auto le permet.",
      },
    ],
  },
};

export default function HowItWorks({ pageKey }) {
  const [open, setOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const config = PAGES[pageKey];
  if (!config) return null;

  const toggle = (q) => setExpandedItems((prev) => ({ ...prev, [q]: !prev[q] }));

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-sm italic text-muted-foreground hover:text-primary transition-colors group"
        data-testid={`how-it-works-btn-${pageKey}`}
        title="Comment ça marche ?"
      >
        <HelpCircle className="w-4 h-4 opacity-70 group-hover:opacity-100" />
        <span className="underline decoration-dotted underline-offset-2">Comment ça marche ?</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid={`how-it-works-modal-${pageKey}`}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <HelpCircle className="w-5 h-5 text-primary" />
              {config.title}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Réponses aux questions les plus fréquentes sur cette section
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 mt-2">
            {config.items.map((item) => {
              const isOpen = !!expandedItems[item.q];
              return (
                <div
                  key={item.q}
                  className="border border-white/10 rounded-lg overflow-hidden bg-white/[0.02]"
                >
                  <button
                    type="button"
                    onClick={() => toggle(item.q)}
                    className="w-full p-3 text-left flex items-center gap-3 hover:bg-white/5 transition-colors"
                    data-testid={`hiw-q-${pageKey}-${item.q.substring(0, 25).replace(/\s+/g, '-')}`}
                  >
                    <ChevronDown
                      className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                    <span className="text-sm font-medium">{item.q}</span>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-3 pl-11 text-sm text-muted-foreground whitespace-pre-line leading-relaxed border-t border-white/5 pt-2">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
